/**
 * Clinic-local calendar arithmetic — pure. Instants on the wire are ISO
 * UTC; every grid, day boundary and weekday is computed in the CLINIC'S
 * timezone (the same clinic-local convention the backend's availability
 * engine and Smart Scheduling use). Display calendar (Jalali/Gregorian) is
 * presentation only and lives in core/calendar/format.ts.
 */

import type { BookingView } from '@/core/clinic/bookingsApi';
import { toAsciiDigits } from '@/core/i18n/digits';

const MS = 60_000;

export interface LocalParts {
  y: number;
  m: number;
  d: number;
  minuteOfDay: number;
  /** ISO weekday 1 = Monday … 7 = Sunday. */
  dayOfWeek: number;
}

const WEEKDAY_ISO: Record<string, number> = {
  Mon: 1,
  Tue: 2,
  Wed: 3,
  Thu: 4,
  Fri: 5,
  Sat: 6,
  Sun: 7,
};

export function localParts(instant: Date, tz: string): LocalParts {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    weekday: 'short',
    hourCycle: 'h23',
  }).formatToParts(instant);
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? '';
  return {
    y: Number(get('year')),
    m: Number(get('month')),
    d: Number(get('day')),
    minuteOfDay: Number(get('hour')) * 60 + Number(get('minute')),
    dayOfWeek: WEEKDAY_ISO[get('weekday')] ?? 1,
  };
}

function offsetMinutes(instant: Date, tz: string): number {
  const p = localParts(instant, tz);
  const asUtc = Date.UTC(p.y, p.m - 1, p.d, Math.floor(p.minuteOfDay / 60), p.minuteOfDay % 60);
  return Math.round((asUtc - instant.getTime()) / MS);
}

/** UTC instant of clinic-local (y, m, d, minuteOfDay); DST-safe two-pass. */
export function instantFromLocal(
  y: number,
  m: number,
  d: number,
  minuteOfDay: number,
  tz: string,
): Date {
  const guess = new Date(Date.UTC(y, m - 1, d, 0, minuteOfDay));
  const first = new Date(guess.getTime() - offsetMinutes(guess, tz) * MS);
  return new Date(guess.getTime() - offsetMinutes(first, tz) * MS);
}

/** 'YYYY-MM-DD' (Gregorian, clinic-local) of an instant. */
export function localDateISO(instant: Date, tz: string): string {
  const p = localParts(instant, tz);
  return `${p.y}-${String(p.m).padStart(2, '0')}-${String(p.d).padStart(2, '0')}`;
}

export function parseDateISO(iso: string): { y: number; m: number; d: number } | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return null;
  return { y: Number(m[1]), m: Number(m[2]), d: Number(m[3]) };
}

/**
 * True only for a real Gregorian 'YYYY-MM-DD'. Stricter than parseDateISO:
 * an out-of-range month or day (2026-02-31) is rejected instead of letting
 * Date arithmetic roll it into a silently shifted day. Anything arriving
 * from a URL or free-form input is sanitized with this BEFORE reaching the
 * strict math — dayBounds et al. keep throwing on bad input by design.
 */
export function isValidDateISO(iso: string | null | undefined): iso is string {
  if (!iso) return false;
  const p = parseDateISO(iso);
  if (!p) return false;
  const d = new Date(Date.UTC(p.y, p.m - 1, p.d));
  return d.getUTCFullYear() === p.y && d.getUTCMonth() === p.m - 1 && d.getUTCDate() === p.d;
}

/** Shift a Gregorian 'YYYY-MM-DD' by whole days (calendar arithmetic, no tz). */
export function shiftDateISO(iso: string, days: number): string {
  const p = parseDateISO(iso);
  if (!p) return iso;
  const d = new Date(Date.UTC(p.y, p.m - 1, p.d + days));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;
}

/** [start, end) instants of one clinic-local day. */
export function dayBounds(dateISO: string, tz: string): { from: Date; to: Date } {
  const p = parseDateISO(dateISO);
  if (!p) throw new Error('bad date');
  return {
    from: instantFromLocal(p.y, p.m, p.d, 0, tz),
    to: instantFromLocal(p.y, p.m, p.d + 1, 0, tz),
  };
}

/** ISO weekday of a Gregorian date (independent of tz). */
export function weekdayOfDateISO(dateISO: string): number {
  const p = parseDateISO(dateISO);
  if (!p) return 1;
  const js = new Date(Date.UTC(p.y, p.m - 1, p.d)).getUTCDay(); // 0=Sun
  return js === 0 ? 7 : js;
}

/**
 * The seven dates of the week containing `dateISO`, starting on `weekStart`
 * (ISO weekday; 6 = Saturday for Jalali clinics, 1 = Monday otherwise).
 */
export function weekDates(dateISO: string, weekStart: number): string[] {
  const wd = weekdayOfDateISO(dateISO);
  const back = (wd - weekStart + 7) % 7;
  const first = shiftDateISO(dateISO, -back);
  return Array.from({ length: 7 }, (_, i) => shiftDateISO(first, i));
}

export const weekStartFor = (calendarSystem: string): number =>
  calendarSystem === 'jalali' ? 6 : 1;

/** The visible grid (design rule): 08:00–22:00 behind a scroll. */
export const GRID_START_MINUTE = 8 * 60;
export const GRID_END_MINUTE = 22 * 60;

export interface Placed<T> {
  item: T;
  /** Minutes from the grid start; may be negative / clipped by the caller. */
  top: number;
  height: number;
  /** Overlap lane index and lane count for side-by-side rendering. */
  lane: number;
  lanes: number;
}

/** Position timed items within one clinic-local day; overlaps get lanes. */
export function layoutDay<T extends { startsAt: string; endsAt: string }>(
  items: readonly T[],
  dateISO: string,
  tz: string,
): Placed<T>[] {
  const { from } = dayBounds(dateISO, tz);
  const base = from.getTime();
  const sorted = [...items].sort((a, b) => Date.parse(a.startsAt) - Date.parse(b.startsAt));
  const placed: Placed<T>[] = [];
  // Greedy lane assignment inside overlap clusters.
  let cluster: Placed<T>[] = [];
  let clusterEnd = -Infinity;
  const flush = () => {
    const lanes = cluster.reduce((m, p) => Math.max(m, p.lane + 1), 0);
    for (const p of cluster) p.lanes = lanes;
    cluster = [];
  };
  for (const it of sorted) {
    const s = (Date.parse(it.startsAt) - base) / MS;
    const e = (Date.parse(it.endsAt) - base) / MS;
    const top = s - GRID_START_MINUTE;
    const height = Math.max(e - s, 15);
    if (s >= clusterEnd) flush();
    const taken = new Set(cluster.filter((p) => p.top + p.height > top).map((p) => p.lane));
    let lane = 0;
    while (taken.has(lane)) lane += 1;
    const p: Placed<T> = { item: it, top, height, lane, lanes: 1 };
    cluster.push(p);
    placed.push(p);
    clusterEnd = Math.max(clusterEnd, e);
  }
  flush();
  return placed;
}

export interface Zone {
  kind: 'outside' | 'absence';
  /** Minutes from grid start. */
  top: number;
  height: number;
  label?: string;
}

/** Grey "outside working hours" zones for one weekday from weekly windows. */
export function outsideHoursZones(
  windows: readonly { dayOfWeek: number; startMinute: number; endMinute: number }[],
  dayOfWeek: number,
): Zone[] {
  const day = windows
    .filter((w) => w.dayOfWeek === dayOfWeek)
    .sort((a, b) => a.startMinute - b.startMinute);
  const zones: Zone[] = [];
  let cursor = GRID_START_MINUTE;
  for (const w of day) {
    if (w.startMinute > cursor)
      zones.push({
        kind: 'outside',
        top: cursor - GRID_START_MINUTE,
        height: w.startMinute - cursor,
      });
    cursor = Math.max(cursor, w.endMinute);
  }
  if (cursor < GRID_END_MINUTE)
    zones.push({
      kind: 'outside',
      top: cursor - GRID_START_MINUTE,
      height: GRID_END_MINUTE - cursor,
    });
  return zones.filter((z) => z.height > 0);
}

/** Hatched absence zones (time off / blockout) intersecting one local day. */
export function absenceZones(
  absences: readonly { startsAt: string; endsAt: string; reason?: string | null }[],
  dateISO: string,
  tz: string,
): Zone[] {
  const { from, to } = dayBounds(dateISO, tz);
  const out: Zone[] = [];
  for (const a of absences) {
    const s = Math.max(Date.parse(a.startsAt), from.getTime());
    const e = Math.min(Date.parse(a.endsAt), to.getTime());
    if (e <= s) continue;
    const top = (s - from.getTime()) / MS - GRID_START_MINUTE;
    const height = (e - s) / MS;
    out.push({ kind: 'absence', top, height, label: a.reason ?? undefined });
  }
  return out;
}

export type ServiceGroup = 'speech' | 'ot' | 'assess' | 'coach' | 'other';

/**
 * The controlled colour set (design 5A): a FRONTEND visual grouping over
 * clinic-created service names — never a taxonomy; the name is always
 * written on the block. Keyword heuristics in EN and FA; unknown → neutral.
 */
export function serviceGroup(name: string): ServiceGroup {
  const n = name.toLowerCase();
  if (/speech|گفتار/.test(n)) return 'speech';
  if (/occupational|\bot\b|کاردرمان/.test(n)) return 'ot';
  if (/assess|consult|ارزیاب|مشاوره/.test(n)) return 'assess';
  if (/coach|parent|والدین|آموزش/.test(n)) return 'coach';
  return 'other';
}

export const isGhost = (b: Pick<BookingView, 'status'>): boolean => b.status !== 'booked';

/** The next upcoming booked session after `now`, if any. */
export function nextUpcoming<T extends { startsAt: string; status: string }>(
  items: readonly T[],
  now: Date,
): T | null {
  const upcoming = items
    .filter((b) => b.status === 'booked' && Date.parse(b.startsAt) >= now.getTime())
    .sort((a, b) => Date.parse(a.startsAt) - Date.parse(b.startsAt));
  return upcoming[0] ?? null;
}

export function minutesBetween(a: string, b: string): number {
  return Math.round((Date.parse(b) - Date.parse(a)) / MS);
}

export const hhmm = (minuteOfDay: number): string =>
  `${String(Math.floor(minuteOfDay / 60)).padStart(2, '0')}:${String(minuteOfDay % 60).padStart(2, '0')}`;

export function hhmmToMinute(v: string): number | null {
  // ZOY-314: Persian/Arabic-Indic digits count as digits here too.
  const m = /^(\d{1,2}):(\d{2})$/.exec(toAsciiDigits(v.trim()));
  if (!m) return null;
  const h = Number(m[1]);
  const mm = Number(m[2]);
  if (h > 23 || mm > 59) return null;
  return h * 60 + mm;
}
